
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Palette, Globe, Upload, Crown } from "lucide-react";

interface WhiteLabelSettingsProps {
  organizationId: string;
  currentTier: string;
}

interface BrandingSettings {
  primaryColor: string;
  secondaryColor: string;
  logoUrl: string;
  companyName: string;
  customDomain: string;
}

export const WhiteLabelSettings = ({ organizationId, currentTier }: WhiteLabelSettingsProps) => {
  const { toast } = useToast();
  const [branding, setBranding] = useState<BrandingSettings>({
    primaryColor: '#3b82f6',
    secondaryColor: '#1e40af',
    logoUrl: '',
    companyName: '',
    customDomain: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const isWhiteLabelEnabled = currentTier === 'white_label' || currentTier === 'enterprise';

  useEffect(() => {
    loadBrandingSettings();
  }, [organizationId]);

  const loadBrandingSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('branding')
        .eq('id', organizationId)
        .single();

      if (error) throw error;

      if (data?.branding && typeof data.branding === 'object') {
        const existingBranding = data.branding as Record<string, any>;
        setBranding(prevBranding => ({
          ...prevBranding,
          ...existingBranding
        }));
      }
    } catch (error) {
      console.error('Error loading branding settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!isWhiteLabelEnabled) {
      toast({
        title: "Upgrade Required",
        description: "White-label features require a White-Label or Enterprise plan.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSaving(true);

      // Convert BrandingSettings to a plain object that matches Json type
      const brandingData = {
        primaryColor: branding.primaryColor,
        secondaryColor: branding.secondaryColor,
        logoUrl: branding.logoUrl,
        companyName: branding.companyName,
        customDomain: branding.customDomain
      };

      const { error } = await supabase
        .from('organizations')
        .update({ branding: brandingData })
        .eq('id', organizationId);

      if (error) throw error;

      toast({
        title: "Settings Saved",
        description: "Your branding settings have been updated successfully.",
      });
    } catch (error) {
      console.error('Error saving branding settings:', error);
      toast({
        title: "Error",
        description: "Failed to save branding settings",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="text-center py-4">Loading branding settings...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Palette className="h-5 w-5" />
          White-Label Branding
          {isWhiteLabelEnabled ? (
            <Badge className="bg-purple-100 text-purple-800">
              <Crown className="h-3 w-3 mr-1" />
              Enabled
            </Badge>
          ) : (
            <Badge variant="secondary">
              Upgrade Required
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {!isWhiteLabelEnabled && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Crown className="h-5 w-5 text-purple-600" />
              <span className="font-medium text-purple-800">White-Label Features</span>
            </div>
            <p className="text-sm text-purple-700 mb-3">
              Customize CSO Agent with your own branding, logo, and domain. Available with White-Label and Enterprise plans.
            </p>
            <Button className="bg-purple-600 hover:bg-purple-700">
              Upgrade to White-Label
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Company Branding */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="companyName">Company Name</Label>
              <Input
                id="companyName"
                value={branding.companyName}
                onChange={(e) => setBranding({ ...branding, companyName: e.target.value })}
                placeholder="Your Company Name"
                disabled={!isWhiteLabelEnabled}
              />
            </div>

            <div>
              <Label htmlFor="logoUrl">Logo URL</Label>
              <div className="flex gap-2">
                <Input
                  id="logoUrl"
                  value={branding.logoUrl}
                  onChange={(e) => setBranding({ ...branding, logoUrl: e.target.value })}
                  placeholder="https://example.com/logo.png"
                  disabled={!isWhiteLabelEnabled}
                />
                <Button variant="outline" disabled={!isWhiteLabelEnabled}>
                  <Upload className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Color Scheme */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="primaryColor">Primary Color</Label>
              <div className="flex gap-2">
                <Input
                  id="primaryColor"
                  type="color"
                  value={branding.primaryColor}
                  onChange={(e) => setBranding({ ...branding, primaryColor: e.target.value })}
                  className="w-16 h-10 p-1"
                  disabled={!isWhiteLabelEnabled}
                />
                <Input
                  value={branding.primaryColor}
                  onChange={(e) => setBranding({ ...branding, primaryColor: e.target.value })}
                  placeholder="#3b82f6"
                  disabled={!isWhiteLabelEnabled}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="secondaryColor">Secondary Color</Label>
              <div className="flex gap-2">
                <Input
                  id="secondaryColor"
                  type="color"
                  value={branding.secondaryColor}
                  onChange={(e) => setBranding({ ...branding, secondaryColor: e.target.value })}
                  className="w-16 h-10 p-1"
                  disabled={!isWhiteLabelEnabled}
                />
                <Input
                  value={branding.secondaryColor}
                  onChange={(e) => setBranding({ ...branding, secondaryColor: e.target.value })}
                  placeholder="#1e40af"
                  disabled={!isWhiteLabelEnabled}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Custom Domain */}
        <div>
          <Label htmlFor="customDomain" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Custom Domain
          </Label>
          <Input
            id="customDomain"
            value={branding.customDomain}
            onChange={(e) => setBranding({ ...branding, customDomain: e.target.value })}
            placeholder="yourcompany.com"
            disabled={!isWhiteLabelEnabled}
          />
          <p className="text-xs text-gray-500 mt-1">
            Contact support to configure your custom domain
          </p>
        </div>

        {/* Preview */}
        {isWhiteLabelEnabled && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium mb-3">Preview</h4>
            <div 
              className="border rounded-lg p-4 bg-white"
              style={{ 
                borderColor: branding.primaryColor,
                borderWidth: '2px'
              }}
            >
              <div className="flex items-center gap-3 mb-3">
                {branding.logoUrl && (
                  <img 
                    src={branding.logoUrl} 
                    alt="Logo" 
                    className="h-8 w-8 object-contain"
                  />
                )}
                <h3 
                  className="font-bold text-lg"
                  style={{ color: branding.primaryColor }}
                >
                  {branding.companyName || 'Your Company'}
                </h3>
              </div>
              <Button 
                style={{ 
                  backgroundColor: branding.primaryColor,
                  borderColor: branding.primaryColor 
                }}
                className="text-white"
              >
                Sample Button
              </Button>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <Button 
            onClick={handleSave}
            disabled={isSaving || !isWhiteLabelEnabled}
            className="flex items-center gap-2"
          >
            {isSaving ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
